<template>
  <div>
    <div class="panel m-3">
      <p class="panel-heading">
        {{ groupName }}
      </p>
      <div class="container pt-3 pb-3">
        <div
          v-if="hasContactData"
          class="stammdaten"
        >
          <div class="stammdaten-facts">
            <p v-if="email">
              {{ email }}
            </p>
            <p v-if="phone1Line">
              {{ phone1Line }}
            </p>
            <p v-if="phone2Line">
              {{ phone2Line }}
            </p>
            <p v-if="addressLine">
              {{ addressLine }}
            </p>
          </div>
        </div>
        <template v-if="chosenClients.length > 0 || chosenUsers.length > 0">
          <hr
            v-if="hasContactData"
            class="my-2"
          >
          <div class="member-columns">
            <div
              v-if="chosenClients.length > 0"
              class="member-column"
            >
              <b-table
                class="member-table"
                :data="chosenClients"
                narrowed
                striped
                hoverable
                @click="openClient"
              >
                <b-table-column
                  v-slot="props"
                  label="Klienten"
                  cell-class="has-text-left"
                >
                  {{ props.row.attributes.first_name }}
                  {{ props.row.attributes.last_name }}
                </b-table-column>
              </b-table>
            </div>
            <div
              v-if="chosenUsers.length > 0"
              class="member-column"
            >
              <b-table
                class="member-table"
                :data="chosenUsers"
                narrowed
                striped
                hoverable
                @click="openUser"
              >
                <b-table-column
                  v-slot="props"
                  label="Mitarbeiter"
                  cell-class="has-text-left"
                >
                  {{ props.row.attributes.first_name }}
                  {{ props.row.attributes.last_name }}
                </b-table-column>
              </b-table>
            </div>
          </div>
        </template>
      </div>
    </div>

    <section class="mx-5 my-2">
      <div
        class="buttons"
        style="display: flex"
      >
        <b-button
          class="is-primary"
          tag="router-link"
          to="/groups"
        >
          Zurück
        </b-button>
        <b-button
          class="is-danger"
          style="margin-left: auto"
          @click="triggerDelete"
        >
          Löschen
        </b-button>
        <b-button
          class="is-primary"
          @click="triggerEdit"
        >
          Bearbeiten
        </b-button>
      </div>
    </section>
    <ModalComponent
      v-if="isDeleteCheck && group"
      :item="'die Gruppe ' + groupName"
      :is-delete-check="isDeleteCheck"
      :is-loading="isLoading"
      :cancel="() => isDeleteCheck = false"
      :submit="submitDelete"
    />
    <b-modal v-model="groupInUsageWarning">
      <div
        v-if="groupInUsageWarning"
        class="panel m-3"
        style="background-color: white"
      >
        <p class="panel-heading">
          Du kannst die Gruppe "{{ groupName }}" erst löschen nachdem
          alle Klienten und Mitarbeiter aus der Gruppe entfernt wurden!
        </p>
        <div
          class="buttons mt-2"
          style="display: flex; justify-content: center"
        >
          <b-button
            class="is-primary"
            @click="groupInUsageWarning = false"
          >
            Okay
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
/**
 * This file is for viewing a focused group
 */
import {queryClients, queryGroups, queryUserMetaClasses} from "../../lib/Query";
import {negativeFeedBack, positiveFeedBack} from "@/lib/Toast";
import ModalComponent from "@/components/helperComponents/ModalComponent";

export default {
	name: "ViewGroup",
	components: {ModalComponent},
	data() {
		return {
			groupName: "",
			chosenClients: [],
			chosenUsers: [],
			group: undefined,
			isDeleteCheck: false,
			isLoading: false,
			groupInUsageWarning: false
		}
	},
	computed: {
		email() {
			const email = this.group && this.group.attributes.email;
			return email ? email.trim() : "";
		},
		phone1Line() {
			if (!this.group) return "";
			const a = this.group.attributes;
			return this.formatPhone(a.phone1, a.info1);
		},
		phone2Line() {
			if (!this.group) return "";
			const a = this.group.attributes;
			return this.formatPhone(a.phone2, a.info2);
		},
		addressLine() {
			if (!this.group) return "";
			const a = this.group.attributes;
			const city = [ a.plz, a.location ]
				.filter(Boolean).map(s => s.trim()).filter(Boolean).join(" ");
			const street = a.street ? a.street.trim() : "";
			return [ street, city ].filter(Boolean).join(", ");
		},
		hasContactData() {
			return !!(this.email || this.phone1Line || this.phone2Line ||
				this.addressLine);
		}
	},
	async created() {
		const groups = await queryGroups()
		const clients = await queryClients()
		const users = await queryUserMetaClasses()
		let group = this.$route.params.data
		// Reached via browser navigation (back/forward, reload, deep
		// link): the in-memory object is gone, look it up by the URL id.
		if (!group && this.$route.params.id) {
			group = groups.find(g => g.id === this.$route.params.id)
		}
		this.group = group
		if (group) {
			this.groupName = group.attributes.group_name
			this.$store.dispatch("setDetailTitle", this.groupName)
			clients.forEach(client => {
				if(client.attributes.group.id === group.id){
					this.chosenClients.push(client)
				}
			})
			users.forEach(user => {
				const groupIds = user.attributes.groups ? user.attributes.groups.map(group => group.id): []
				if(groupIds.includes(group.id)){
					this.chosenUsers.push(user)
				}
			})
		}else{
			await this.$router.push("/groups")
		}
	},
	methods: {
		formatPhone(phone, info) {
			phone = phone ? phone.trim() : "";
			info = info ? info.trim() : "";
			if (!phone) return "";
			return info ? `${phone} (${info})` : phone;
		},
		openClient(client) {
			// A click that ends a drag-selection selects text, it should
			// not navigate.
			const selection = window.getSelection && window.getSelection()
			if (selection && selection.toString().length > 0) return
			this.$router.push(
				{ name: "ViewClient", params: { id: client.id, data: client } });
		},
		openUser(user) {
			const selection = window.getSelection && window.getSelection()
			if (selection && selection.toString().length > 0) return
			this.$router.push(
				{ name: "ViewUser", params: { id: user.id, data: user } });
		},
		triggerEdit() {
			this.$store.dispatch("setGroupId", this.group.id);
			this.$router.push({ name: "AddGroup", params: { data: this.group } });
		},
		triggerDelete() {
			if (this.chosenClients.length > 0 || this.chosenUsers.length > 0) {
				this.groupInUsageWarning = true;
			}
			else {
				this.isDeleteCheck = true;
			}
		},
		async submitDelete() {
			this.isLoading = true;
			try {
				await this.group.destroy();
				this.$store.dispatch("groups/delete", this.group);
				positiveFeedBack("Gruppe gelöscht!");
				this.isDeleteCheck = false;
				await this.$router.push("/groups");
			}
			catch (e) {
				console.error(e);
				negativeFeedBack("Gruppe nicht gelöscht!");
			}
			finally {
				this.isLoading = false;
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.stammdaten {
	display: flex;
	align-items: flex-start;
	gap: 1.5rem;
	padding-left: 1.5rem;
}

.stammdaten-facts {
	text-align: left;
	min-width: 0;
}

.stammdaten-facts p {
	margin-bottom: 0.25rem;
}

.member-columns {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 1.5rem;
	padding: 0 1.5rem;
}

.member-column {
	flex: 1;
	min-width: 16rem;
}

.member-table ::v-deep tbody tr {
	cursor: pointer;
}

@media screen and (max-width: 768px) {
	.member-table {
		padding: 0 0.75rem;
	}
}
</style>
